import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from './useMaterialsConsumptionQueries'
import sapConsumptionService from '../../../../../services/sapConsumptionService'
import { api } from '../../../../../lib/api'

// ---------------------------------------------------------------------------
// Helper interno: construye el objeto de contexto mínimo necesario
// para saber qué invalidar en onSuccess.
// ---------------------------------------------------------------------------

/**
 * Crea las llamadas de invalidación comunes para consumos + períodos de la orden.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {{ factory, prodLine, orderNumber, periodID }} ctx
 */
function invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID }) {
  const promises = [
    queryClient.invalidateQueries({ queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.hopperPeriods(factory, prodLine, orderNumber) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.consumptionsFallback(factory, prodLine, orderNumber) }),
  ]
  if (periodID) {
    promises.push(
      queryClient.invalidateQueries({ queryKey: queryKeys.periodConsumptions(periodID) })
    )
  }
  return Promise.all(promises)
}

// ===========================================================================
// ██████  ██████  ███    ██ ███████ ██    ██ ███    ███  ██████  ███████
// ██      ██    ██ ████   ██ ██      ██    ██ ████  ████ ██    ██ ██
// ██      ██    ██ ██ ██  ██ ███████ ██    ██ ██ ████ ██ ██    ██ ███████
// ██      ██    ██ ██  ██ ██      ██ ██    ██ ██  ██  ██ ██    ██      ██
//  ██████  ██████  ██   ████ ███████  ██████  ██      ██  ██████  ███████
// ===========================================================================
// Consumos (add / update / remove / adjust)
// ===========================================================================

/**
 * Añade un consumo al período activo.
 *
 * @param {{ factory, prodLine, orderNumber, periodID }} orderCtx  Contexto de la orden actual
 */
export function useAddConsumptionMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID, componentSapCode, dosingUnit, hopper }) =>
      sapConsumptionService.addConsumption(periodID, componentSapCode, dosingUnit, hopper),

    onSuccess: () => {
      const { factory, prodLine, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })
      // La receta puede cambiar porque el backend actualiza cantidades declaradas
      queryClient.invalidateQueries({
        queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, true),
      })
    },
  })
}

/**
 * Elimina un consumo del período.
 */
export function useRemoveConsumptionMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID, dosingUnit, hopper }) =>
      sapConsumptionService.removeConsumption(periodID, dosingUnit, hopper),

    onSuccess: () => {
      const { factory, prodLine, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })
    },
  })
}

/**
 * Actualiza un consumo (edición de componente / dosificador / hopper).
 */
export function useUpdateConsumptionMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      periodID,
      componentSapCode,
      dosingUnit,
      hopper,
      originalDosingUnit,
      originalHopper,
      createdAt,
      updatedAt,
    }) =>
      sapConsumptionService.updateConsumption(
        periodID,
        componentSapCode,
        dosingUnit,
        hopper,
        originalDosingUnit,
        originalHopper,
        createdAt,
        updatedAt
      ),

    onSuccess: () => {
      const { factory, prodLine, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })
    },
  })
}

/**
 * Ajusta la cantidad de consumo de una tolva/dosificador.
 */
export function useAdjustHopperConsumptionMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID, dosingUnit, hopper, adjustedQuantity }) =>
      sapConsumptionService.adjustHopperConsumption(periodID, dosingUnit, hopper, adjustedQuantity),

    onSuccess: () => {
      const { factory, prodLine, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })
    },
  })
}

// ===========================================================================
// Períodos (calculate / sendToSAP / updateEnd / finalizeAndNext)
// ===========================================================================

/**
 * Calcula consumos del período desde InfluxDB.
 */
export function useCalculatePeriodConsumptionsMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID }) =>
      sapConsumptionService.calculatePeriodConsumptions(periodID),

    onSuccess: () => {
      const { factory, prodLine, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })
    },
  })
}

/**
 * Envía el período a SAP.
 */
export function useSendPeriodToSAPMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID }) =>
      sapConsumptionService.sendPeriodToSAP(periodID),

    onSuccess: () => {
      const { factory, prodLine, sapCode, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })
      if (factory && prodLine && sapCode) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, false, 'pending'),
        })
        queryClient.invalidateQueries({
          queryKey: ['orders', factory, prodLine],
        })
      }
      // La receta puede reflejar cantidades enviadas
      queryClient.invalidateQueries({
        queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, true),
      })
    },
  })
}

/**
 * Actualiza la fecha de fin de un período.
 */
export function useUpdatePeriodEndMutation(orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID, newPeriodEnd }) =>
      sapConsumptionService.updatePeriodEnd(periodID, newPeriodEnd),

    onSuccess: () => {
      const { factory, prodLine, orderNumber } = orderCtx ?? {}
      queryClient.invalidateQueries({
        queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber),
      })
    },
  })
}

/**
 * Finaliza el período activo y crea el siguiente.
 * El backend devuelve info del nuevo período en la respuesta.
 *
 * @param {Function} onNewPeriod  Callback opcional con el nuevo período (para setActivePeriod)
 */
export function useFinalizePeriodAndNextMutation(orderCtx, onNewPeriod) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ periodID }) =>
      sapConsumptionService.finalizePeriodAndNext(periodID),

    onSuccess: (data) => {
      const { factory, prodLine, orderNumber, periodID } = orderCtx ?? {}
      invalidateConsumptionAndPeriods(queryClient, { factory, prodLine, orderNumber, periodID })

      // Si el backend devuelve el nuevo período, notificarlo al caller
      if (onNewPeriod && data) {
        onNewPeriod(data)
      }
    },
  })
}

// ===========================================================================
// Órdenes (start / finish / updateDates)
// ===========================================================================

/**
 * Inicia una orden de producción (crea el período 1 automáticamente).
 */
export function useStartProductionOrderMutation(lineCtx, orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ factory, prodLine, orderNumber, startedAt }) =>
      sapConsumptionService.startProductionOrder(factory, prodLine, orderNumber, startedAt),

    onSuccess: () => {
      const { factory, prodLine, sapCode } = lineCtx ?? {}
      const { orderNumber } = orderCtx ?? {}
      if (factory && prodLine && sapCode) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, false, 'pending'),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, true, 'pending'),
        })
      }
      if (factory && prodLine && orderNumber) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber),
        })
      }
    },
  })
}

/**
 * Finaliza una orden de producción (cierra OF y último período activo).
 */
export function useFinishProductionOrderMutation(lineCtx, orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ factory, prodLine, orderNumber }) =>
      sapConsumptionService.finishProductionOrder(factory, prodLine, orderNumber),

    onSuccess: () => {
      const { factory, prodLine, sapCode } = lineCtx ?? {}
      const { orderNumber } = orderCtx ?? {}
      if (factory && prodLine && sapCode) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, false, 'pending'),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, true, 'pending'),
        })
      }
      if (factory && prodLine && orderNumber) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, true),
        })
      }
    },
  })
}

/**
 * Actualiza las fechas de inicio y/o fin de una orden.
 */
export function useUpdateOrderDatesMutation(lineCtx, orderCtx) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ factory, prodLine, orderNumber, startDate, endDate, orderSnapshot }) =>
      sapConsumptionService.updateOrderDates(factory, prodLine, orderNumber, startDate, endDate, {
        orderSnapshot,
      }),

    onSuccess: () => {
      const { factory, prodLine, sapCode } = lineCtx ?? {}
      const { orderNumber } = orderCtx ?? {}
      if (factory && prodLine && sapCode) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, false, 'pending'),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, true, 'pending'),
        })
      }
      if (factory && prodLine && orderNumber) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, true),
        })
      }
    },
  })
}

/**
 * Trae la receta desde SAP (SapRequest true) y escribe el resultado en la query canónica (fromSAP false)
 * para que `useOrderRecipeQuery` refleje los componentes sin salir de la pantalla.
 */
export function usePullOrderRecipeFromSapMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ factory, prodLine, orderNumber }) =>
      sapConsumptionService.getOrderRecipe(factory, prodLine, orderNumber, true),

    onSuccess: (recipe, { factory, prodLine, orderNumber }) => {
      queryClient.setQueryData(
        queryKeys.orderRecipe(factory, prodLine, orderNumber, false),
        recipe
      )
    },
  })
}

// ===========================================================================
// Hook compuesto: todas las mutations de consumo en un solo objeto
// Conveniente cuando el componente las necesita todas juntas.
// ===========================================================================

/**
 * Agrupa todas las mutations relacionadas con consumos y períodos.
 *
 * @param {{ factory, prodLine, sapCode, orderNumber, periodID }} orderCtx
 * @param {Function} [onNewPeriod]  Callback para `finalizeAndNext`
 */
export function useMaterialsConsumptionMutations(orderCtx, onNewPeriod) {
  const addConsumption = useAddConsumptionMutation(orderCtx)
  const removeConsumption = useRemoveConsumptionMutation(orderCtx)
  const updateConsumption = useUpdateConsumptionMutation(orderCtx)
  const adjustHopperConsumption = useAdjustHopperConsumptionMutation(orderCtx)

  const calculateConsumptions = useCalculatePeriodConsumptionsMutation(orderCtx)
  const sendToSAP = useSendPeriodToSAPMutation(orderCtx)
  const updatePeriodEnd = useUpdatePeriodEndMutation(orderCtx)
  const finalizeAndNext = useFinalizePeriodAndNextMutation(orderCtx, onNewPeriod)

  const startOrder = useStartProductionOrderMutation(
    { factory: orderCtx?.factory, prodLine: orderCtx?.prodLine, sapCode: orderCtx?.sapCode },
    { orderNumber: orderCtx?.orderNumber }
  )
  const finishOrder = useFinishProductionOrderMutation(
    { factory: orderCtx?.factory, prodLine: orderCtx?.prodLine, sapCode: orderCtx?.sapCode },
    { orderNumber: orderCtx?.orderNumber }
  )
  const updateOrderDates = useUpdateOrderDatesMutation(
    { factory: orderCtx?.factory, prodLine: orderCtx?.prodLine, sapCode: orderCtx?.sapCode },
    { orderNumber: orderCtx?.orderNumber }
  )

  return {
    addConsumption,
    removeConsumption,
    updateConsumption,
    adjustHopperConsumption,
    calculateConsumptions,
    sendToSAP,
    updatePeriodEnd,
    finalizeAndNext,
    startOrder,
    finishOrder,
    updateOrderDates,
  }
}
